import { useEffect, useRef, useState } from "preact/hooks";
import { daemonAddress } from "@transport";

import * as auth from "../lib/auth";
import * as Icon from "./Icons";
import { ServerAddress } from "./ServerAddress";

/**
 * What the form is asking for.
 *
 * Two steps and not two screens: the password fields stay mounted and keep
 * their values when the second one appears, which is the whole of the
 * difference between this and a wizard. See {@link stepAfter}.
 */
export type LoginStep = "password" | "code";

/**
 * Which step a failed sign-in leaves the form on.
 *
 * Exported and pure so it can be tested without a DOM -- the same arrangement
 * as `shapeOf` in `AttachmentView.tsx`, and for the same reason: this is the
 * decision, and the JSX below is a rendering of it.
 *
 * Two things hang off the answer and both are about not making a person type
 * something twice.
 *
 * **`code` means the daemon asked a question rather than refused an answer.**
 * `totp_required` covers both "you did not give me a code" and "that code was
 * not accepted", deliberately: they are the same request repeated, and the
 * second must not throw away the password. A form that reset to step one on a
 * mistyped digit would charge somebody their whole password for a fat finger --
 * on the screen where their password manager has already filled the field and
 * will not fill it again without a reload.
 *
 * **`totp_throttled` stays on the code step too, even though it is a refusal.**
 * The answer to "too many codes" is to wait and try one more, and dropping back
 * to the password would suggest the password was the problem.
 *
 * Anything else -- a wrong password, an unknown user, a 502, a dead daemon --
 * is step one, because step two cannot help with any of them.
 *
 * **`null` is "stay exactly where you are, and keep what has been typed".** It
 * has one cause: {@link auth.CredentialStoreError}, where the daemon accepted
 * both halves and this machine's credential store would not keep the result.
 * Neither field was wrong, so neither may be charged for it -- and the second
 * step is where that matters, because dropping back to the password would cost
 * somebody a fresh six digits from their phone as well, for a locked keychain.
 */
export function stepAfter(error: unknown): LoginStep | null {
  if (error instanceof auth.CredentialStoreError) return null;
  const problem = auth.problemOf(error);
  return problem === auth.TOTP_REQUIRED || problem === auth.TOTP_THROTTLED ? "code" : "password";
}

/**
 * What the person reads under the fields when a sign-in did not finish.
 *
 * Ordinarily the server's own sentence, verbatim: it is deliberately the same
 * for a wrong password and an unknown user, and it is the one thing that
 * separates "I have not seen a code" from "that code was not accepted".
 * Rewriting it here would invent distinctions the daemon refused to make.
 *
 * A credential store that refused is the case that needs a sentence of its own,
 * because the *shape* of what happened is not what a login error usually is:
 * the password was accepted and the sign-in still did not happen. Three things
 * have to be in it, and each was missing from the incident this was written
 * for, where the screen said nothing at all:
 *
 * - that the password was not the problem, so nobody goes hunting for a typo;
 * - why they are still looking at this form, which is that a credential this
 *   app cannot store is one it cannot open mail with -- see `lib/auth.ts`;
 * - **the store's own words**, quoted rather than summarised. "User interaction
 *   is not allowed" is the entire diagnosis on a Mac launched outside its Aqua
 *   session, and no sentence written here could have guessed it.
 *
 * The mechanism is not named. It is a keychain on macOS, Secret Service on
 * Linux, the Credential Manager on Windows and a file in the app sandbox on
 * Android, and a sentence that said "keychain" would be wrong on two of them.
 */
export function sentenceFor(error: unknown): string {
  if (error instanceof auth.CredentialStoreError) {
    return (
      "Signed in, but the credential could not be saved on this device, and without it " +
      `this app cannot open your mail. The store said: ${error.reason}`
    );
  }
  return error instanceof Error ? error.message : String(error);
}

/**
 * The sign-in screen.
 *
 * Replaces an overlay that sat on top of a fully-mounted mail UI and told
 * people to shell into the server and copy a 78-character token. This renders
 * *instead of* the app, because a mailbox drawn behind a login prompt is a
 * mailbox someone can see the shape of without signing in.
 *
 * Everything here is JS-driven. The CSP that ships with this app sets
 * `form-action 'none'`, so a form that submits natively is blocked outright —
 * hence `preventDefault` rather than an action attribute.
 */
export function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Always false where the box is not drawn, which is what keeps this one piece
  // of state honest across the two builds: the desktop build never renders
  // {@link PublicComputer}, so nothing can set it, so `login` is asked for an
  // ordinary session there without anything having to check which build it is.
  const [shared, setShared] = useState(false);
  // Absent until the daemon asks for it. Drawing the field up front would ask
  // the majority of people for something they do not have, and would be the
  // client guessing at a fact only the daemon holds -- whether this account has
  // a second factor, and whether this address is one it exempts.
  const [step, setStep] = useState<LoginStep>("password");
  const [code, setCode] = useState("");
  const codeField = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    void auth.refreshCapability();
  }, []);

  /*
   * The cursor follows the question.
   *
   * The field arrives after a round trip, below the button that was just
   * pressed, and on a phone it arrives below the fold. Somebody who has to find
   * it and tap it has been asked to do the work of noticing that the form
   * changed -- so this is not a nicety: it is the difference between the second
   * step being a step and being an obstacle.
   *
   * Keyed on `step` rather than on the ref, so it fires when the step arrives
   * and not again on every keystroke afterwards -- a focus call on each render
   * would fight the person's own cursor if they tabbed away.
   */
  useEffect(() => {
    if (step === "code") codeField.current?.focus();
  }, [step]);

  const cap = auth.capability.value;

  async function submit(e: Event) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      // `undefined` and not `""` when there is nothing to send: see `login`,
      // which leaves the field out of the body entirely rather than sending an
      // empty one.
      await auth.login(username, password, shared, code.trim() || undefined);
    } catch (err) {
      // Usually the server's own message, verbatim, and the one case that is
      // not is the one where the server had nothing to do with it. See
      // {@link sentenceFor}.
      setError(sentenceFor(err));
      const next = stepAfter(err);
      // Nothing typed was wrong, so nothing typed is thrown away: the form
      // stays on whichever step it was on, with the password and any code
      // still in it. See {@link stepAfter}.
      if (next === null) return;
      setStep(next);
      // Cleared either way, because a code that has been refused is a code that
      // will not work a second time: the window it belonged to has passed. What
      // is *not* cleared is the password, unless the form is going back to
      // asking for one -- see {@link stepAfter}.
      setCode("");
      if (next === "password") setPassword("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="login">
      <div class="login-card">
        <div class="login-brand">
          <Icon.Logo />
          <strong>HydraMail</strong>
        </div>

        {cap && !cap.loginsConfigured ? (
          <NoLogins />
        ) : (
          <>
            {cap && !cap.passwordLogin && <InsecureTransport />}
            {/* Before the credentials, because it decides where they are sent.
                Draws nothing in a browser. */}
            <ServerAddress onChanged={() => void auth.refreshCapability()} />
            <form onSubmit={submit}>
              <div class="field">
                <label for="u">Username</label>
                <input
                  id="u"
                  name="username"
                  type="text"
                  // Named so a password manager can find them. The device-token
                  // field this replaces used autocomplete="off", which is right
                  // for a pasted token and actively unhelpful for a password
                  // someone has to remember.
                  autocomplete="username"
                  autoFocus
                  required
                  value={username}
                  disabled={busy}
                  onInput={(e) => setUsername((e.target as HTMLInputElement).value)}
                />
              </div>
              <div class="field">
                <label for="p">Password</label>
                <input
                  id="p"
                  name="password"
                  type="password"
                  autocomplete="current-password"
                  required
                  value={password}
                  disabled={busy}
                  onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
                />
              </div>

              {step === "code" && (
                <div class="field">
                  <label for="otp">Code from your authenticator app</label>
                  <input
                    id="otp"
                    name="one-time-code"
                    ref={codeField}
                    // `text` and not `number`: a code is a string of six digits
                    // and not a quantity, and `number` brings spinner arrows, a
                    // locale's thousands separator and a field that silently
                    // drops a leading zero -- which one code in ten begins with.
                    type="text"
                    // The numeric keypad on a phone, without making the field a
                    // number. The two are separate settings precisely because
                    // this case exists.
                    inputMode="numeric"
                    // The whole reason to name it: iOS and Android offer the
                    // code straight from the SMS or the authenticator, and
                    // Safari fills it without anyone leaving the app.
                    autocomplete="one-time-code"
                    maxLength={6}
                    // Not `required`: the button below is already gated on it,
                    // and a native validation bubble on a field that arrived
                    // mid-flow reads as a second error beside the server's one.
                    value={code}
                    disabled={busy}
                    onInput={(e) => setCode((e.target as HTMLInputElement).value)}
                  />
                  <span class="hint">
                    Six digits from your authenticator app.
                  </span>
                </div>
              )}

              {/* Under the password and above the button, so it is read as part
                  of signing in rather than as a setting somebody could come
                  back to. There is no coming back to it: it decides what kind
                  of session the next click creates.

                  Below the code field as well, and it stays mounted across the
                  two steps: the box is a statement about the session the *next*
                  click creates, and one that unticked itself when the daemon
                  asked for a code would quietly mint a thirty-day session on a
                  library machine at exactly the moment somebody was paying
                  attention to something else. */}
              <PublicComputer checked={shared} disabled={busy} onChange={setShared} />

              {error && (
                <p class="login-error" role="alert">
                  {error}
                </p>
              )}

              {/* The code counts towards "is there anything to send" only once
                  it has been asked for. Gating on it always would disable the
                  button on the first step, where there is no field to fill. */}
              <button
                class="btn primary"
                type="submit"
                disabled={busy || !username || !password || (step === "code" && !code.trim())}
              >
                {busy ? "Signing in…" : step === "code" ? "Sign in with code" : "Sign in"}
              </button>
            </form>

          </>
        )}
      </div>
    </div>
  );
}

/**
 * "This is a public or shared computer."
 *
 * Drawn only in a browser, and `daemonAddress === null` is how that is asked --
 * the same test `ServerAddress` makes in the other direction, a few lines above
 * this in the same form. It is the right question rather than a convenient one:
 * what the box promises is that this *origin's storage* is left alone and that
 * the cookie dies with the browser, and neither sentence means anything in a
 * build whose credential is in the OS keychain and whose window is not a tab
 * anybody else will open.
 *
 * The hint is one line and says the two things somebody deciding needs: nothing
 * is kept here, and it does not last. It deliberately does not explain *what*
 * would otherwise be kept -- a list of storage keys is not what the person at
 * the library computer is trying to find out.
 */
function PublicComputer({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}) {
  if (daemonAddress !== null) return null;
  return (
    // The words are inside the label, so the hit area is the sentence rather
    // than a 13px square -- the rule `.acct-check` exists for, and the reason
    // this reuses it instead of laying out a field.
    <label class="acct-check" style={{ marginTop: 12 }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange((e.target as HTMLInputElement).checked)}
      />
      <span>
        This is a public or shared computer
        <div class="hint">
          Nothing is kept on this machine. The session ends in 12 hours or when the browser
          closes.
        </div>
      </span>
    </label>
  );
}

/**
 * A daemon nobody has a password for yet.
 *
 * Offering a form here would be offering one that cannot succeed no matter what
 * is typed into it, which sends someone hunting for a password they never had.
 * Naming the command is the honest answer — the same choice the Accounts screen
 * makes, and for the same reason: provisioning is a CLI act today.
 */
function NoLogins() {
  return (
    <>
      <h1>No one can sign in yet</h1>
      <p class="lede">
        The daemon is running and has no passwords set. On the machine running hydramaild:
      </p>
      <div class="code">hydramaild user set-password &lt;tenant&gt;</div>
      {/*
        It takes no password argument, deliberately: one on a command line is
        visible in `ps` and lands in the shell history. The screen only needs to
        say that a prompt is coming.
      */}
      <p class="hint" style={{ marginTop: 12 }}>
        It prompts for the password, and does not echo it.
      </p>
    </>
  );
}

/**
 * Said before anyone types, not after.
 *
 * The daemon will refuse this login, and the refusal arrives once the password
 * has already been typed into a page delivered over plain HTTP. Warning up
 * front is the difference between "this server is not set up for passwords" and
 * "I appear to have got my own password wrong".
 */
function InsecureTransport() {
  return (
    <div class="banner error" style={{ marginBottom: 14 }}>
      <Icon.Warning />
      <span>
        This page was not served over HTTPS, so the daemon will refuse a password. Put TLS in front
        of it and set <code>HM_EXTERNAL_URL</code>, or reach it on <code>localhost</code>.
      </span>
    </div>
  );
}
